import { Button, Divider, message, notification, Progress, Typography, Upload, UploadProps } from "antd";
import { RcFile } from "antd/es/upload";
import { api } from "api";
import { ApiService } from "api/api.service";
import { T_Api_Success_Res } from "api/api.types";
import { IconDocx, IconPdf } from "assets/icons/onboarding";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { SingleQueryWrapper } from "components/QueryWrapper";
import OnboardingLayout from "layout/OnboardingLayout";
import { Fragment, ReactElement, useState } from "react";
import { TbBuildingBank, TbChevronLeft, TbChevronRight, TbFileText, TbTrash } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { AvailableResources } from "types/permissionGroup.types";
import { MAX_FILE_UPLOAD_SIZE } from "utils/constants";
import { delay, getErrorMessage, humanFileSize } from "utils/functions";
import { storedTokens } from "utils/storedTokens";
import ApplicationRedirect from "../components/ApplicationRedirect";
import styles from "../Onboarding.module.scss";
// import { queryClient } from "utils/queryClient";
// import CounterText from "components/CounterText";
import { ONBOARDING_ROUTES } from "utils/routes";
import PageTitle from "components/PageTitle";
import { APPLICATION_STATUS } from "types/application.types";
import DashboardLayout from "layout/DashboardLayout";

const extensionIcons: Record<string, ReactElement> = {
  docx: <IconDocx />,
  pdf: <IconPdf />,
};

export function UploadBankDocs() {
  const navigate = useNavigate();
  const applicationId = storedTokens.selectedApplicationId.getToken();
  const allBanks = ApiService.onboarding.useGetArtifacts(applicationId, "banks");
  const updateStatus = ApiService.onboarding.useUpdateStatus(applicationId);

  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [directProceed, setDirectProceed] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const [uploadPercent, setUploadPercent] = useState<number>();
  const hasExisting =
    allBanks?.data &&
    allBanks?.data?.result?.length > 0 &&
    allBanks?.data?.result?.map((el) => el.attachments).flat().length > 0;

  // const [progress, setProgress] = useState<Record<string, number>>({});

  const commitBank = ApiService.onboarding.useCommitArtifact("banks");
  const props: UploadProps = {
    name: "files",
    accept: ".pdf",
    multiple: true,
    beforeUpload: (file) => {
      const isLtMaxSize = file.size / 1024 / 1024 < MAX_FILE_UPLOAD_SIZE;
      const isSameFile = fileList.find((el) => {
        return el.name === file.name && el.size === file.size;
      });
      if (!isLtMaxSize) {
        message.error(`File must smaller than ${MAX_FILE_UPLOAD_SIZE}MB!`);
      } else if (isSameFile) {
        message.error("File with same name and same size already exists. Please rename the file and re-upload");
      } else {
        setFileList((prev) => [...prev, file]);
      }

      return Upload.LIST_IGNORE;
    },
  };

  if (!applicationId) {
    return <ApplicationRedirect />;
  }

  return (
    <DashboardLayout>
      <OnboardingLayout loader={{ isLoading: isLoading, desc: "Our bot is checking your docs" }}>
        <PageTitle pageTitle={[]} title="Upload Bank Docs" />
        <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5">
          {fileList.length ? (
            <div className="flex items-center justify-between">
              <Typography.Title className="!mb-0" level={5}>
                New files
              </Typography.Title>
              <Upload {...props}>
                <Button icon={<TbFileText />} variant="filled" color="primary" className="border-purple-300">
                  Upload Files
                </Button>
              </Upload>
            </div>
          ) : (
            <Upload.Dragger className={styles.file_upload} showUploadList={false} {...props}>
              <div className="bg-white flex flex-col items-center justify-center">
                <img role="presentation" className="max-w-[400px]" src="/doc-upload.png" />
                <Typography.Title level={5}>Tell us about your finances</Typography.Title>
                <Typography.Text type="secondary">
                  Drag & drop bank statements. Max size {MAX_FILE_UPLOAD_SIZE}MB. Supported file types .pdf
                </Typography.Text>
                <Button className="mt-4">Select Files</Button>
              </div>
            </Upload.Dragger>
          )}

          <div className="flex flex-col gap-3 mb-[98px]">
            {fileList.map((file) => {
              const x = file.name?.split(".");
              const extension = x[x?.length - 1];
              const fileName = file?.name;

              return (
                <div
                  key={file.uid}
                  className="bg-white rounded-xl border border-solid border-gray-300 flex items-center gap-3 p-3"
                >
                  <div className="text-3xl">{extensionIcons[extension]}</div>
                  <div className="flex gap-3 justify-between w-full">
                    <div className="">
                      <Typography.Title level={5} className="!mb-1">
                        {fileName}
                      </Typography.Title>
                      <Typography.Text type="secondary">{humanFileSize(file?.size ?? 0)}</Typography.Text>
                    </div>
                    {/* {progress?.[file?.uid] ? <Progress percent={progress?.[file?.uid]} /> : null} */}
                  </div>
                  <Button
                    variant="text"
                    color="danger"
                    danger
                    icon={<TbTrash />}
                    onClick={() => {
                      setFileList((prev) => {
                        return prev.filter((el) => el.uid !== file.uid);
                      });
                    }}
                  />
                </div>
              );
            })}

            <SingleQueryWrapper
              query={allBanks}
              showLoaderOn="isFetching"
              renderData={(banks) => {
                return (
                  <>
                    {hasExisting ? (
                      <>
                        <Typography.Title className="mt-4" level={5}>
                          Existing files
                        </Typography.Title>
                        {banks?.result?.map((bank) => {
                          return (
                            <Fragment key={bank._id}>
                              {bank.attachments.map((file) => {
                                const x = file.original_file_name?.split(".");
                                const extension = x[x?.length - 1];
                                const fileName = file.original_file_name;
                                return (
                                  <div
                                    key={file._id}
                                    className="bg-white rounded-xl border border-solid border-gray-300 flex items-center gap-3 p-3"
                                  >
                                    <div className="text-3xl">{extensionIcons[extension]}</div>
                                    <div className="flex gap-3 justify-between w-full">
                                      <div className="">
                                        <Typography.Title level={5} className="!mb-1">
                                          {fileName}
                                        </Typography.Title>
                                        <Typography.Text type="secondary">
                                          {humanFileSize(file?.size ?? 0)}
                                        </Typography.Text>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </Fragment>
                          );
                        })}
                      </>
                    ) : null}
                    {/* {!hasExisting && fileList.length === 0 ? (
                    <div className="flex flex-col gap-4">
                      <Divider>Or</Divider>
                      <Button
                        onClick={() => navigate(ONBOARDING_ROUTES.UploadBankDocs_2)}
                        variant="filled"
                        color="primary"
                        className="border-purple-300"
                        icon={<TbBuildingBank />}
                      >
                        Add Bank Manually
                      </Button>
                    </div>
                  ) : null} */}
                  </>
                );
              }}
            />
          </div>
        </div>

        <div className="fixed left-0 right-0 bottom-0 flex justify-between bg-white py-3 px-5 border-t border-solid border-[0px] border-gray-300 ">
          <Button
            size="large"
            icon={<TbChevronLeft />}
            onClick={() => {
              navigate(ONBOARDING_ROUTES.UploadLoanDocs_3);
            }}
          >
            Back
          </Button>
          {/* {fileList.length || hasExisting ? (
          <Button
            size="large"
            // type="primary"
            // icon={<TbChevronRight />}
            // iconPosition="end"
            className="w-fit ml-auto mr-4"
            loading={
              typeof uploadPercent !== "undefined" && uploadPercent !== 100
                ? {
                    icon: (
                      <Progress
                        percent={uploadPercent}
                        showInfo={false}
                        type="circle"
                        trailColor={"#ffffff66"}
                        size={18}
                        strokeColor={"white"}
                      />
                    ),
                  }
                : isLoading
            }
            onClick={() => {
              if (fileList.length) {
                setDirectProceed(false);
                const formData = new FormData();
                fileList.forEach((file) => {
                  formData.append("files", file);
                });
                formData.append("category", AvailableResources.BANK);

                const config: AxiosRequestConfig = {
                  // handles the upload progress
                  onUploadProgress: (event) => {
                    // const percent = Math.floor((event.loaded / (event?.total ?? 1000)) * 100);
                    setUploadPercent((event.progress ?? 0) * 100);
                    setIsLoading(true);
                  },
                };

                // post request to the api
                api
                  .post("/file/upload", formData, config)
                  .then((res: AxiosResponse<T_Api_Success_Res<{ attachments: string[] }>>) => {
                    const resFile = res.data.result;
                    // message.success("File upload successful");
                    commitBank.mutate(
                      {
                        applicationId: applicationId,
                        body: {
                          attachments: resFile?.attachments,
                        },
                      },
                      {
                        onSuccess: (data) => {
                          return delay(data.result.refreshInMilis, async () => {
                            console.log("here");

                            const prevBankCount = allBanks.data?.result?.length ?? 0;
                            console.log(allBanks.data?.result?.length, prevBankCount);
                            // const newBanks = await allBanks.refetch();

                            await queryClient.invalidateQueries({
                              queryKey: ["application", "artifact/all", "banks", applicationId],
                            });
                            const newBankCount = allBanks.data?.result?.length ?? 0;

                            if (newBankCount > prevBankCount) {
                              setIsLoading(false);

                              navigate(ONBOARDING_ROUTES.UploadBankDocs_2);
                            } else {
                              notification.info({
                                message: `All bank statements might not have been processed`,
                                description: (
                                  <>
                                    Please check in next step. Redirecting to next step in <CounterText seconds={3} />{" "}
                                    seconds
                                  </>
                                ),
                              });
                              await delay(3000, () => {
                                setIsLoading(false);
                                navigate(ONBOARDING_ROUTES.UploadBankDocs_2);
                              });
                            }
                          });
                        },
                        onError: () => {
                          setIsLoading(false);
                        },
                      },
                    );
                  })
                  .catch((error) => {
                    message.error(getErrorMessage(error));
                    setIsLoading(false);
                  });
              } else if (hasExisting && fileList.length === 0) {
                navigate(ONBOARDING_ROUTES.UploadBankDocs_2);
              }
            }}
          >
            <span>View Uploaded Data</span>
          </Button>
        ) : null} */}
          {fileList.length || hasExisting ? (
            <Button
              size="large"
              type="primary"
              icon={<TbChevronRight />}
              iconPosition="end"
              className="w-fit"
              loading={
                typeof uploadPercent !== "undefined" && uploadPercent !== 100
                  ? {
                      icon: (
                        <Progress
                          percent={uploadPercent}
                          showInfo={false}
                          type="circle"
                          trailColor={"#ffffff66"}
                          size={18}
                          strokeColor={"white"}
                        />
                      ),
                    }
                  : isLoading
              }
              onClick={() => {
                if (fileList.length) {
                  const formData = new FormData();
                  fileList.forEach((file) => {
                    formData.append("files", file);
                  });
                  formData.append("category", AvailableResources.BANK);

                  const config: AxiosRequestConfig = {
                    // handles the upload progress
                    onUploadProgress: (event) => {
                      // const percent = Math.floor((event.loaded / (event?.total ?? 1000)) * 100);
                      setUploadPercent((event.progress ?? 0) * 100);
                      setIsLoading(true);
                    },
                  };

                  // post request to the api
                  api
                    .post("/file/upload", formData, config)
                    .then((res: AxiosResponse<T_Api_Success_Res<{ attachments: string[] }>>) => {
                      const resFile = res.data.result;
                      // message.success("File upload successful");
                      commitBank.mutate(
                        {
                          applicationId: applicationId,
                          body: {
                            attachments: resFile?.attachments,
                          },
                        },
                        {
                          onSuccess: () => {
                            updateStatus.mutate(
                              {
                                status: APPLICATION_STATUS.BANKS,
                              },
                              {
                                onSuccess: () => {
                                  navigate(ONBOARDING_ROUTES.ChoosePlan);
                                },
                              },
                            );
                          },
                          onError: () => {
                            setIsLoading(false);
                          },
                        },
                      );
                    })
                    .catch((error) => {
                      message.error(getErrorMessage(error));
                      setIsLoading(false);
                    });
                } else if (hasExisting && fileList.length === 0) {
                  navigate(ONBOARDING_ROUTES.ChoosePlan);
                }
              }}
            >
              <span>Continue</span>
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              onClick={() => {
                navigate(ONBOARDING_ROUTES.ChoosePlan);
              }}
            >
              Skip for now
            </Button>
          )}
        </div>
      </OnboardingLayout>
    </DashboardLayout>
  );
}
